import type {
  ApiDataProcessorType,
  ApiReturnDataProcessorConfiguration,
  ApiReturnDevice,
  ApiReturnDeviceTypeMeasureType,
  ApiReturnMeasurePoint,
  ApiReturnMeasureType,
} from '@highimpact-frontend/api-types';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import {
  DataGridPro,
  GRID_DETAIL_PANEL_TOGGLE_COL_DEF,
  GridColDef,
} from '@mui/x-data-grid-pro';
import DataProcessorConfigurationService from '../../../api/services/dataprocessors/DataProcessorConfiguration/DataProcessorConfigurationService';
import DataProcessorTypeService from '../../../api/services/dataprocessors/DataProcessorType/DataProcessorTypeService';
import CreateAlertConfiguration from '../../../components/alerts/CreateAlertConfiguration';
import ConfirmModal from '../../../components/utils/confirmModal/ConfirmModal';
import { useModal } from '../../../utils/hooks/useModal';
import { useSubmit } from '../../../utils/hooks/useSubmit';
import { useCallback, useEffect, useState } from 'react';
import { useTheme } from '@mui/material/styles';
// import { INDICATOR_NORMAL, INDICATOR_WARNING, INDICATOR_ERROR, INDICATOR_CRITICAL } from '../../../assets/mui-themes/clear-theme';
import { useTranslation } from 'react-i18next';
import PermissionDecoratorV2 from '../../../components/utils/permission/PermissionDecoratorV2';
import AlertConfigActionsCell from './AlertConfigActionsCell';
import { useMuiDataGridLocale } from '../../../i18n/useMuiDataGridLocale';

type RowMUI = {
  id: number;
  data: ApiReturnDataProcessorConfiguration;
  measurePoint: string;
  measureType: string;
  configType: string;
};

interface MonitoringListProps {
  projectId: number;
  siteId: number;
  monitoringData: ApiReturnDataProcessorConfiguration[];
  monitoringTypes: ApiDataProcessorType[];
  devices: ApiReturnDevice[];
  deviceTypeMeasureTypes: ApiReturnDeviceTypeMeasureType[];
  measureTypes: ApiReturnMeasureType[];
  stations: ApiReturnMeasurePoint[];
  onChange: () => void;
}

const AlertConfigListHelper: React.FC<MonitoringListProps> = ({
  projectId,
  siteId,
  monitoringData,
  monitoringTypes,
  stations,
  devices,
  deviceTypeMeasureTypes,
  measureTypes,
  onChange,
}) => {
  const createModal = useModal();
  const updateModal = useModal();
  const deleteModal = useModal<{
    callback: () => void;
    message: string;
    configId: number;
  }>();
  const { submit } = useSubmit();
  const theme = useTheme();
  const { t } = useTranslation(['alerts', 'commons']);
  const localeText = useMuiDataGridLocale();

  const deleteConfiguration = async (id: number) => {
    submit({
      callbacks: [
        async () =>
          await DataProcessorConfigurationService.delete(projectId, siteId, id),
        onChange,
      ],
      errorMessages: {
        anyError: t(
          'Une erreur est survenue lors de la suppression de la configuration.'
        ),
        unicityError: t('Impossible de supprimer cette configuration.'),
      },
      successMessage: t('La configuration a été supprimée avec succès.'),
    });
  };

  const tDeleteMessage = t('Voulez-vous supprimer cette configuration');

  const headingsPrettyNames: Record<string, string> = {};
  const [selectedForUpdate, setSelectedForUpdate] =
    useState<ApiReturnDataProcessorConfiguration | null>(null);

  const rows: RowMUI[] = monitoringData.map((md) => {
    const configTypeObj = monitoringTypes.find(
      (mt) => mt.id === md.dataprocessor_type_id
    );

    return {
      id: md.id,
      data: md,
      measurePoint:
        stations.find((s) => s.device_site?.id === md.device_site_id)?.name ??
        t('Station inconnue') + ' ' + md.device_site_id,
      measureType:
        measureTypes.find((mt) => mt.id === md.measure_type_id)?.name ??
        t('Aucune'),
      configType: configTypeObj
        ? t(configTypeObj.name)
        : t('Aucune'),
    };
  });

  const createColumn = (field: keyof RowMUI): GridColDef<RowMUI> => ({
    field,
    headerName: t(headingsPrettyNames[field]) ?? t(field),
    flex: 1,
    minWidth: 100,
    sortable: true,
    filterable: true,
    headerClassName: 'list-data-grid-style_table-header',
  });

  const columns: GridColDef<RowMUI>[] = [
    GRID_DETAIL_PANEL_TOGGLE_COL_DEF,
    {
      field: 'measurePoint',
      headerName: t('Station de mesure'),
      sortable: true,
      minWidth: 100,
      flex: 1,
      headerClassName: 'list-data-grid-style_table-header',
    },
    {
      field: 'measureType',
      headerName: t('Mesure surveillée'),
      sortable: true,
      minWidth: 100,
      flex: 1,
      headerClassName: 'list-data-grid-style_table-header',
    },
    {
      field: 'configType',
      headerName: t("Type d'alerte"),
      sortable: true,
      minWidth: 100,
      flex: 1,
      headerClassName: 'list-data-grid-style_table-header',
    },
    ...(Object.keys(headingsPrettyNames) as (keyof RowMUI)[]).map((key) =>
      createColumn(key)
    ),
    {
      field: 'actions',
      headerName: t('Actions'),
      minWidth: 100,
      filterable: false,
      sortable: false,
      headerClassName: 'list-data-grid-style_table-header mui-actions-header',
      renderCell: (params) => (
        <AlertConfigActionsCell
          params={params}
          onEdit={(config) => {
            setSelectedForUpdate(config);
            updateModal.open();
          }}
          onDelete={(id) => {
            deleteModal.open({
              callback: () => deleteConfiguration(id),
              message: tDeleteMessage,
              configId: id,
            });
          }}
        />
      ),
    },
  ];

  return (
    <div className={`list-container`}>
      <div className="toolbar">
        <PermissionDecoratorV2 item={'alerts_configurations_create_button'}>
          <button
            type="submit"
            className="uk-button uk-button-default"
            onClick={() => createModal.open()}
          >
            {t('Configurer une alerte')}
          </button>
        </PermissionDecoratorV2>
      </div>

      <Box
        sx={{
          height: 'calc(100vh - 80px - 200px)',
          overflow: 'auto',
          width: '100%',
          maxWidth: '100%',
          marginTop: '20px',
        }}
      >
        <DataGridPro
          localeText={localeText}
          rows={rows}
          columns={columns}
          checkboxSelection={false}
          getDetailPanelContent={(params) => {
            const formatKeyLabel = (key: string): string =>
              key
                .replace(/_/g, ' ')
                .replace(/^./, (char) => char.toUpperCase());

            const {
              start_temporization,
              end_temporization,
              specific_configuration,
            } = params.row.data;

            const hasSpecificConfig =
              specific_configuration &&
              Object.keys(specific_configuration).length > 0;

            return (
              <Box sx={{ p: 2 }}>
                {/* Specific configuration */}
                {!hasSpecificConfig ? (
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                    }}
                  >
                    <Typography variant="body2" sx={{ pl: 1 }}>
                      {t('Pas de configuration spécifique')}
                    </Typography>
                  </Box>
                ) : (
                  <Box
                    sx={{
                      pl: '2.5%',
                      display: 'flex',
                      alignItems: 'center',
                      width: '30%',
                      overflow: 'hidden',
                    }}>
                    <DataGridPro
                      getRowHeight={() => 35}
                      rows={Object.entries({
                        [t("Temporisation d'entrée")]: start_temporization,
                        [t("Temporisation de sortie")]: end_temporization,
                        ...specific_configuration
                      }).map(([key, value], index) => ({
                        id: index,
                        key: t(formatKeyLabel(key)),
                        value: t(String(value)),
                      }))}
                      columns={[
                        { field: 'key', headerName: '', flex: 1 },
                        { field: 'value', headerName: '', flex: 1 },
                      ]}
                      hideFooter
                      slots={{
                        columnHeaders: () => null,
                      }}
                      disableColumnMenu
                      sx={{
                        border: 'none',
                        '& .MuiDataGrid-virtualScroller': {
                          scrollbarWidth: 'none',
                          msOverflowStyle: 'none',
                        },
                        '& .MuiDataGrid-virtualScroller::-webkit-scrollbar': {
                          display: 'none',
                        },
                        '& .MuiDataGrid-scrollbar': {
                          display: 'none',
                        },
                      }}
                    />
                  </Box>
                )}
              </Box>
            );
          }}
          getDetailPanelHeight={() => 'auto'}
          slotProps={{
            columnMenu: {
              slots: {
                columnMenuSortItem: null,
                columnMenuColumnsItem: null,
                columnMenuPinningItem: null,
              },
            },
          }}
          initialState={{
            sorting: {
              sortModel: [{ field: 'configType', sort: 'asc' }],
            },
          }}
        />
      </Box>
      {
        deleteModal.isOpen && (
          <ConfirmModal
            onCancel={deleteModal.close}
            onConfirm={() => deleteModal.params?.callback?.()}
            open={deleteModal.isOpen}
          >
            <p>{deleteModal.params?.message!}</p>
          </ConfirmModal>
        )
      }
      {
        createModal.isOpen && (
          <CreateAlertConfiguration
            mode="create"
            isOpen={createModal.isOpen}
            projectId={projectId}
            siteId={siteId}
            closeModal={() => {
              createModal.close();
              onChange();
            }}
            deviceTypeMeasureTypes={deviceTypeMeasureTypes}
            devices={devices}
            measureTypes={measureTypes}
            monitoringData={monitoringTypes}
            stations={stations}
          />
        )
      }
      {
        updateModal.isOpen && selectedForUpdate && (
          <CreateAlertConfiguration
            mode="update"
            configId={selectedForUpdate.id}
            config={selectedForUpdate}
            isOpen={updateModal.isOpen}
            projectId={projectId}
            siteId={siteId}
            closeModal={() => {
              updateModal.close();
              onChange();
            }}
            deviceTypeMeasureTypes={deviceTypeMeasureTypes}
            devices={devices}
            measureTypes={measureTypes}
            monitoringData={monitoringTypes}
            stations={stations}
          />
        )
      }
    </div>
  );
};

const AlertConfigList = ({
  project_id,
  site_id,
  stations,
  devices,
  deviceTypeMeasureTypes,
  measureTypes,
}: {
  project_id: number;
  site_id: number;
  devices: ApiReturnDevice[];
  deviceTypeMeasureTypes: ApiReturnDeviceTypeMeasureType[];
  measureTypes: ApiReturnMeasureType[];
  stations: ApiReturnMeasurePoint[];
}) => {
  const [configData, setConfigData] = useState<
    ApiReturnDataProcessorConfiguration[]
  >([]);
  const [configTypes, setConfigTypes] = useState<ApiDataProcessorType[]>([]);
  const [loadingConfigs, setLoadingConfigs] = useState(false);
  const { t } = useTranslation('alerts');

  const fetchConfigs = useCallback(async (): Promise<void> => {
    setLoadingConfigs(true);
    try {
      const list = await DataProcessorConfigurationService.getAll(
        project_id,
        site_id
      );
      setConfigData(list);
    } catch (err) {
      console.error('Error fetching configurations:', err);
    } finally {
      setLoadingConfigs(false);
    }
  }, [project_id, site_id]);

  const fetchTypes = useCallback(async (): Promise<void> => {
    try {
      const list = await DataProcessorTypeService.getAll(project_id);
      setConfigTypes(list);
    } catch (err) {
      console.error('Error fetching types:', err);
    }
  }, [project_id]);

  useEffect(() => {
    void fetchConfigs();
  }, [fetchConfigs]);

  useEffect(() => {
    void fetchTypes();
  }, [fetchTypes]);
  return loadingConfigs === true ? (
    <Box
      display="flex"
      alignItems="center"
      justifyContent="center"
      flexDirection="column"
      height="100%"
      sx={{ p: 2 }}
    >
      <CircularProgress />
      <Typography variant="body1" sx={{ mt: 2 }}>
        {t('Chargement des configurations...')}
      </Typography>
    </Box>
  ) : (
    <AlertConfigListHelper
      projectId={project_id}
      siteId={site_id}
      monitoringData={configData}
      stations={stations}
      monitoringTypes={configTypes}
      devices={devices}
      deviceTypeMeasureTypes={deviceTypeMeasureTypes}
      measureTypes={measureTypes}
      onChange={fetchConfigs}
    />
  );
};

export default AlertConfigList;
